import { Price } from "@shared/Components";
import { type FunctionalComponent, h } from "preact";
import { useId, useMemo, useState } from "preact/hooks";
import { dispatchMbbAddProduct } from "../ShoppingCart/Helpers/mbb-add-product";
import {
  type Badge,
  buildValueText,
  type DataPackage,
  formatGb,
  hasCampaignPrice,
  hasRouterPriceBefore,
  initialPackageIndex,
  parsePackages,
} from "./mbb-data-selector-utils";

type MbbDataSelectorProps = {
  heading?: string;
  packages?: string;
};

const Icon: FunctionalComponent<{ name: string }> = ({ name }: { name: string }) => (
  <svg className="mbb-data-selector__icon" focusable="false" aria-hidden="true">
    <use xlinkHref={`#${name}`} />
  </svg>
);

const BadgeList: FunctionalComponent<{ badges: Badge[]; placement: "summary" | "header" }> = ({
  badges,
  placement,
}: {
  badges: Badge[];
  placement: "summary" | "header";
}) =>
  badges.length === 0 ? null : (
    <ul className={`badge-list list--unstyled mbb-data-selector__badges mbb-data-selector__badges--${placement}`}>
      {badges.map((badge) => (
        <li key={badge.text} className={`badge ${badge.cssClass}`}>
          {badge.text}
        </li>
      ))}
    </ul>
  );

const PackageSummary: FunctionalComponent<{ pkg: DataPackage }> = ({ pkg }: { pkg: DataPackage }) => (
  <div className="mbb-data-selector__summary">
    <BadgeList badges={pkg.badges} placement="summary" />
    <div className="mbb-data-selector__gb">
      {formatGb(pkg.gb)}
      <span className="mbb-data-selector__gb-unit">GB</span>
    </div>
    {pkg.subtext && <div className="mbb-data-selector__name">{pkg.subtext}</div>}
    <div className="mbb-data-selector__meta">
      {pkg.peopleText && (
        <span className="mbb-data-selector__meta-item">
          <Icon name="ice-user" />
          {pkg.peopleText}
        </span>
      )}
      {pkg.streamText && (
        <span className="mbb-data-selector__meta-item">
          <Icon name="ice-streaming" />
          {pkg.streamText}
        </span>
      )}
    </div>
    <div className="mbb-data-selector__price-row">
      <strong className="mbb-data-selector__price">
        <Price amount={pkg.price} useKr addMnd testIdPrefix="mbb-data-selector-" />
      </strong>
      {hasCampaignPrice(pkg) && (
        <s className="mbb-data-selector__price--was">
          <span className="visually-hidden">Førpris: </span>
          {pkg.priceNoCampaign} kr
        </s>
      )}
    </div>
    {pkg.bindingPeriodInMonths > 0 && (
      <p className="mbb-data-selector__binding">med {pkg.bindingPeriodInMonths} mnd. bindingstid</p>
    )}
    <div className={`mbb-data-selector__router-pill ${pkg.routerCssClass ?? ""}`.trim()}>
      <Icon name="ice-mbb" />
      <span>
        {pkg.routerCampaignText && (
          <span className="mbb-data-selector__router-campaign">{pkg.routerCampaignText}: </span>
        )}
        Ruter <strong>{pkg.routerPrice} kr</strong>
      </span>
      {hasRouterPriceBefore(pkg) && (
        <s className="mbb-data-selector__router-price--was">
          <span className="visually-hidden">Førpris: </span>
          {pkg.routerPriceBefore} kr
        </s>
      )}
    </div>
  </div>
);

export const MbbDataSelector: FunctionalComponent<MbbDataSelectorProps> = ({
  heading,
  packages,
}: MbbDataSelectorProps) => {
  const packagesList = useMemo(() => parsePackages(packages), [packages]);
  const [selectedIndexState, setSelectedIndex] = useState(() => initialPackageIndex(packagesList));
  const selectedIndex = Math.min(selectedIndexState, packagesList.length - 1);
  const [liveText, setLiveText] = useState("");
  const inputId = useId();
  const headingId = useId();

  if (packagesList.length === 0) return null;

  const selected = packagesList[selectedIndex];
  const valueText = buildValueText(selected);
  const atMin = selectedIndex === 0;
  const atMax = selectedIndex === packagesList.length - 1;

  const selectPackage = (index: number, options?: { announce: boolean }) => {
    setSelectedIndex(index);
    if (options?.announce) setLiveText(buildValueText(packagesList[index]));
  };

  const onSliderInput = (event: Event) => {
    selectPackage(Number((event.target as HTMLInputElement).value));
  };

  return (
    <div className="mbb-data-selector__inner">
      {heading && (
        <h2 id={headingId} className="mbb-data-selector__heading">
          {heading}
        </h2>
      )}

      <section
        className="mbb-data-selector__card"
        aria-labelledby={heading ? headingId : undefined}
        aria-label={heading ? undefined : "Datavelger for mobilt bredbånd"}
      >
        <PackageSummary pkg={selected} />

        <div className="mbb-data-selector__slider-header">
          <span aria-hidden="true" className="mbb-data-selector__slider-title">
            Datapakke
          </span>
          <BadgeList badges={selected.badges} placement="header" />
        </div>

        <input
          type="range"
          className="mbb-data-selector__slider"
          min={0}
          max={packagesList.length - 1}
          step={1}
          value={selectedIndex}
          onInput={onSliderInput}
          aria-label="Datapakke i GB"
          aria-valuetext={valueText}
        />
        <div className="mbb-data-selector__ticks" aria-hidden="true">
          {packagesList.map((pkg, index) => (
            <button
              key={pkg.subscriptionOfferingId}
              type="button"
              tabIndex={-1}
              className="mbb-data-selector__tick"
              onClick={() => selectPackage(index)}
            ></button>
          ))}
        </div>
        <div className="mbb-data-selector__labels" aria-hidden="true">
          {packagesList.map((pkg, index) => (
            <button
              key={pkg.subscriptionOfferingId}
              type="button"
              tabIndex={-1}
              className={`mbb-data-selector__label ${index === selectedIndex ? "mbb-data-selector__label--selected" : ""}`}
              onClick={() => selectPackage(index)}
            >
              {formatGb(pkg.gb)}
            </button>
          ))}
        </div>

        <div className="mbb-data-selector__gb-input-row">
          <label htmlFor={inputId} className="mbb-data-selector__gb-input-label">
            Antall GB
          </label>
          <div className="mbb-data-selector__stepper">
            <button
              type="button"
              className="mbb-data-selector__stepper-button"
              onClick={() => !atMin && selectPackage(selectedIndex - 1, { announce: true })}
              aria-disabled={atMin}
              aria-label="Mindre data"
            >
              <Icon name="ice-minus" />
            </button>
            <input
              id={inputId}
              type="text"
              readOnly
              className="mbb-data-selector__gb-input"
              value={formatGb(selected.gb)}
            />
            <button
              type="button"
              className="mbb-data-selector__stepper-button"
              onClick={() => !atMax && selectPackage(selectedIndex + 1, { announce: true })}
              aria-disabled={atMax}
              aria-label="Mer data"
            >
              <Icon name="ice-plus" />
            </button>
          </div>
        </div>

        <div aria-live="polite" className="visually-hidden">
          {liveText}
        </div>

        {packagesList.map((pkg, index) => (
          <div
            key={pkg.subscriptionOfferingId}
            className={`subscription-card__add mbb-data-selector__add ${index === selectedIndex ? "" : "mbb-data-selector__add--hidden"}`}
            data-product-id={pkg.subscriptionOfferingId}
          >
            <div>
              <button
                type="button"
                className="subscription-card__add__button sid-subtitle-2"
                onClick={(event) =>
                  dispatchMbbAddProduct(pkg.bundle, event.currentTarget as HTMLElement, "MBB datavelger")
                }
                aria-label={`Legg til ${buildValueText(pkg)}`}
              >
                Legg til
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};
